"use client";

import { ComponentProps } from "react";
import { useIsShopsite } from "@context/DomainContext";
import { Select as RadixSelect } from "@radix-ui/themes";
import * as SelectPrimitive from "@radix-ui/react-select";
import { joinClassNames } from "@utils/helpers";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";

interface Props {
  options: { value: string; label: string | React.ReactNode }[];
  sharedButtonClasses?: string;
  shopsiteButtonClasses?: string;
  whitelabelButtonClasses?: string;
  placeholder?: string;
  position?: "popper" | "item-aligned";
  triggerContent?: React.ReactNode;
  buttonVariant?: "classic" | "surface" | "soft" | "ghost" | undefined;
}

export default function Select({
  options,
  shopsiteButtonClasses = "",
  whitelabelButtonClasses = "",
  sharedButtonClasses = "",
  placeholder = "",
  position,
  triggerContent,
  buttonVariant,
  ref,
  ...restProps
}: Props &
  ComponentProps<typeof RadixSelect.Root> & {
    ref?: React.Ref<HTMLButtonElement>;
  }) {
  const isShopsite = useIsShopsite();

  const shopsiteButtonBaseClasses =
    "h-[38px] flex justify-between items-center w-full px-3 py-2 focus:py-[7px] focus:px-[11px] text-black bg-white hover:bg-gray-100 border border-gray-300 rounded-md shadow-sm cursor-default focus:outline-none focus:border-2 focus:border-indigo-500 transition-colors duration-100 ease-in-out text-black disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:[&>span]:text-gray-600";

  if (isShopsite) {
    return (
      <SelectPrimitive.Root {...restProps}>
        <SelectPrimitive.Trigger
          ref={ref}
          className={joinClassNames(
            sharedButtonClasses,
            shopsiteButtonBaseClasses,
            shopsiteButtonClasses
          )}
        >
          {triggerContent ?? (
            <>
              <SelectPrimitive.Value placeholder={placeholder} />
              <SelectPrimitive.Icon asChild>
                <ChevronDownIcon
                  className="h-4 w-4 opacity-50"
                  aria-hidden="true"
                />
              </SelectPrimitive.Icon>
            </>
          )}
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position={position}
            className={joinClassNames(
              "relative z-[100] min-w-[8rem] overflow-hidden bg-white border border-gray-300 rounded-md shadow",
              position === "popper" ? "max-h-80" : ""
            )}
          >
            <SelectPrimitive.ScrollUpButton className="flex items-center justify-center h-6 text-black cursor-default">
              <ChevronUpIcon className="w-5 h-5" aria-hidden="true" />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport className={joinClassNames("p-1.5")}>
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className="relative flex items-center pl-6 pr-9 py-1 text-sm text-black rounded select-none focus:outline-none focus:bg-indigo-500 focus:text-indigo-50"
                >
                  <SelectPrimitive.ItemText>
                    {option.label}
                  </SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="absolute left-1 w-6 cursor-default">
                    <CheckIcon className="w-4 h-4" aria-hidden="true" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton className="flex items-center justify-center h-6 text-black cursor-default">
              <ChevronDownIcon className="w-5 h-5" aria-hidden="true" />
            </SelectPrimitive.ScrollDownButton>
            <SelectPrimitive.Arrow />
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  }

  return (
    <RadixSelect.Root {...restProps}>
      <RadixSelect.Trigger
        variant={buttonVariant}
        ref={ref}
        placeholder={placeholder}
        className={joinClassNames(
          sharedButtonClasses,
          isShopsite
            ? joinClassNames(shopsiteButtonBaseClasses, shopsiteButtonClasses)
            : whitelabelButtonClasses
        )}
      >
        {triggerContent}
      </RadixSelect.Trigger>
      <RadixSelect.Content
        className={position === "popper" ? "max-h-80" : ""}
        position={position}
      >
        {options.map((option) => (
          <RadixSelect.Item key={option.value} value={option.value}>
            {option.label}
          </RadixSelect.Item>
        ))}
      </RadixSelect.Content>
    </RadixSelect.Root>
  );
}
